/**
 * Services Page
 * 
 * Allows users to connect and manage their music streaming services
 * Supports: YouTube Music, Spotify
 * Future: Apple Music
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { youtubeAuth } from '../services/youtube';
import { spotifyAuth } from '../services/spotify';
import './Services.css';

function Services({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // YouTube state
  const [youtubeConnected, setYoutubeConnected] = useState(false);
  const [youtubeUser, setYoutubeUser] = useState(null);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  
  // Spotify state
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [spotifyUser, setSpotifyUser] = useState(null);
  const [spotifyLoading, setSpotifyLoading] = useState(false);
  
  const [error, setError] = useState(null);

  // Handle Spotify OAuth callback
  useEffect(() => {
    const handleSpotifyCallback = async () => {
      const code = searchParams.get('code');
      
      // Only process if there's a code AND we haven't already connected
      // Also check if we have a code verifier (meaning we initiated the flow)
      const hasCodeVerifier = localStorage.getItem('jookbox_spotify_code_verifier');
      
      if (code && hasCodeVerifier && !spotifyConnected) {
        setSpotifyLoading(true);
        setError(null);
        
        try {
          await spotifyAuth.handleCallback(code);
          setSpotifyConnected(true);
          
          const userProfile = await spotifyAuth.getUserProfile();
          setSpotifyUser(userProfile);
          
          console.log('✅ Spotify connected successfully');
          
          // Clear the code from URL
          setSearchParams({});
        } catch (err) {
          console.error('Spotify callback error:', err);
          // Only show error if it's not about missing verifier
          if (!err.message.includes('code verifier')) {
            setError(err.message);
          }
          // Clear the code from URL anyway
          setSearchParams({});
        } finally {
          setSpotifyLoading(false);
        }
      } else if (code && !hasCodeVerifier) {
        // Clear stale code from URL
        setSearchParams({});
      }
    };

    handleSpotifyCallback();
  }, [searchParams, setSearchParams, spotifyConnected]);

  // Check connection statuses on mount
  useEffect(() => {
    const checkConnections = async () => {
      // Check YouTube
      if (youtubeAuth.isAuthenticated()) {
        setYoutubeConnected(true);
        try {
          const userInfo = await youtubeAuth.getUserInfo();
          setYoutubeUser(userInfo);
        } catch (err) {
          console.error('Error fetching YouTube user info:', err);
          setYoutubeConnected(false);
        }
      }
      
      // Check Spotify
      if (spotifyAuth.isAuthenticated()) {
        setSpotifyConnected(true);
        try {
          const userProfile = await spotifyAuth.getUserProfile();
          setSpotifyUser(userProfile);
        } catch (err) {
          console.error('Error fetching Spotify user info:', err);
          setSpotifyConnected(false);
        }
      }
    };

    checkConnections();
  }, []);

  // Connect YouTube
  const handleConnectYouTube = async () => {
    setYoutubeLoading(true);
    setError(null);

    try {
      await youtubeAuth.signIn();
      setYoutubeConnected(true);
      
      const userInfo = await youtubeAuth.getUserInfo();
      setYoutubeUser(userInfo);
      
      console.log('✅ YouTube connected successfully');
    } catch (err) {
      console.error('YouTube connection error:', err);
      setError(err.message);
    } finally {
      setYoutubeLoading(false);
    }
  };

  // Disconnect YouTube
  const handleDisconnectYouTube = () => {
    youtubeAuth.signOut();
    setYoutubeConnected(false);
    setYoutubeUser(null);
  };

  // Connect Spotify
  const handleConnectSpotify = async () => {
    setSpotifyLoading(true);
    setError(null);

    try {
      await spotifyAuth.signIn();
      // Will redirect to Spotify, then back here with code
    } catch (err) {
      console.error('Spotify connection error:', err);
      setError(err.message);
      setSpotifyLoading(false);
    }
  };

  // Disconnect Spotify
  const handleDisconnectSpotify = () => {
    spotifyAuth.signOut();
    setSpotifyConnected(false);
    setSpotifyUser(null);
  };

  // Navigate to libraries
  const handleBrowseYouTube = () => navigate('/youtube-library');
  const handleBrowseSpotify = () => navigate('/spotify-library');

  return (
    <div className="services-page">
      <div className="services-container">
        <header className="services-header">
          <h1>🎵 Music Services</h1>
          <p>Connect your music streaming accounts to access all your music in one place</p>
        </header>

        {/* Music Profile Card */}
        <div className="profile-banner" onClick={() => navigate('/music-profile')}>
          <div className="profile-banner-icon">🎯</div>
          <div className="profile-banner-content">
            <h3>Build Your Music Profile</h3>
            <p>Tell us about your background, family influences, and preferences for personalized recommendations</p>
          </div>
          <button className="profile-banner-btn">
            Start Interview →
          </button>
        </div>

        {/* Recommendations Card */}
        <div className="profile-banner recommendations-banner" onClick={() => navigate('/recommendations')}>
          <div className="profile-banner-icon">✨</div>
          <div className="profile-banner-content">
            <h3>Your Personalized Playlists</h3>
            <p>Discover "Mom's Favorites", "Dad's Music", heritage mixes, and more based on your profile</p>
          </div>
          <button className="profile-banner-btn">
            View Recommendations →
          </button>
        </div>

        {error && (
          <div className="services-error">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        <div className="services-grid">
          {/* YouTube Music Card */}
          <div className={`service-card ${youtubeConnected ? 'connected' : ''}`}>
            <div className="service-icon youtube-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </div>
            
            <div className="service-info">
              <h3>YouTube Music</h3>
              {youtubeConnected ? (
                <div className="service-connected-info">
                  <span className="connected-badge">✓ Connected</span>
                  {youtubeUser && (
                    <p className="connected-user">
                      {youtubeUser.thumbnail && (
                        <img src={youtubeUser.thumbnail} alt="" className="user-avatar" />
                      )}
                      {youtubeUser.title}
                    </p>
                  )}
                </div>
              ) : (
                <p className="service-description">
                  Access your YouTube playlists, liked videos, and search for music
                </p>
              )}
            </div>

            <div className="service-actions">
              {youtubeConnected ? (
                <>
                  <button 
                    className="service-btn primary"
                    onClick={handleBrowseYouTube}
                  >
                    Browse Library
                  </button>
                  <button 
                    className="service-btn secondary"
                    onClick={handleDisconnectYouTube}
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button 
                  className="service-btn primary youtube"
                  onClick={handleConnectYouTube}
                  disabled={youtubeLoading}
                >
                  {youtubeLoading ? 'Connecting...' : 'Connect YouTube'}
                </button>
              )}
            </div>
          </div>

          {/* Spotify Card */}
          <div className={`service-card ${spotifyConnected ? 'connected' : ''}`}>
            <div className="service-icon spotify-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
            </div>
            
            <div className="service-info">
              <h3>Spotify</h3>
              {spotifyConnected ? (
                <div className="service-connected-info">
                  <span className="connected-badge spotify-badge">✓ Connected</span>
                  {spotifyUser && (
                    <p className="connected-user">
                      {spotifyUser.image && (
                        <img src={spotifyUser.image} alt="" className="user-avatar" />
                      )}
                      {spotifyUser.name}
                      {spotifyUser.product === 'premium' && (
                        <span className="premium-tag">Premium</span>
                      )}
                    </p>
                  )}
                </div>
              ) : (
                <p className="service-description">
                  Access your Spotify playlists, liked songs, and search millions of tracks
                </p>
              )}
            </div>

            <div className="service-actions">
              {spotifyConnected ? (
                <>
                  <button 
                    className="service-btn primary spotify"
                    onClick={handleBrowseSpotify}
                  >
                    Browse Library
                  </button>
                  <button 
                    className="service-btn secondary"
                    onClick={handleDisconnectSpotify}
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button 
                  className="service-btn primary spotify"
                  onClick={handleConnectSpotify}
                  disabled={spotifyLoading}
                >
                  {spotifyLoading ? 'Connecting...' : 'Connect Spotify'}
                </button>
              )}
            </div>
          </div>

          {/* Apple Music Card (Coming Soon) */}
          <div className="service-card coming-soon">
            <div className="service-icon apple-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.401-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.8.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03c.525 0 1.048-.034 1.57-.1.823-.106 1.597-.35 2.296-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.785-.475-2.035-1.35a1.882 1.882 0 01.107-1.322c.27-.56.728-.916 1.313-1.06.344-.09.695-.14 1.042-.2.37-.06.73-.14 1.058-.33.22-.127.37-.31.4-.564.018-.164.023-.33.024-.496V8.168c0-.186-.05-.36-.236-.424a.728.728 0 00-.214-.04c-.548-.028-1.096-.054-1.644-.082l-3.447-.17c-.168-.01-.336-.018-.504-.036-.2-.02-.324.07-.376.262-.02.07-.03.143-.03.216v6.88c.002.31-.03.616-.127.912-.15.452-.42.82-.84 1.067-.36.21-.756.3-1.164.35-.855.1-1.608-.098-2.2-.753-.43-.47-.543-1.04-.458-1.656.09-.668.447-1.168 1.03-1.5.298-.168.62-.274.955-.34.422-.086.847-.14 1.27-.21.383-.06.758-.13 1.095-.34.22-.14.35-.33.38-.59.01-.08.016-.16.016-.24V5.97c0-.193.035-.38.14-.548.127-.2.318-.297.538-.323.12-.014.24-.015.36-.01.853.04 1.706.08 2.558.12l3.24.16c.452.022.904.043 1.356.066.312.017.466.17.49.485.004.065.004.13.004.195z"/>
              </svg>
            </div>
            <div className="service-info">
              <h3>Apple Music</h3>
              <p className="service-description">Requires $99/year Apple Developer account</p>
            </div>
            <div className="service-actions">
              <button className="service-btn disabled" disabled>
                Coming Soon
              </button>
            </div>
          </div>

          {/* SiriusXM Card */}
          <div className="service-card coming-soon">
            <div className="service-icon sirius-icon">
              <span className="text-icon">SXM</span>
            </div>
            <div className="service-info">
              <h3>SiriusXM</h3>
              <p className="service-description">Requires partnership</p>
            </div>
            <div className="service-actions">
              <button className="service-btn disabled" disabled>
                Not Available
              </button>
            </div>
          </div>

          {/* iHeartRadio Card */}
          <div className="service-card coming-soon">
            <div className="service-icon iheart-icon">
              <span className="text-icon">♥</span>
            </div>
            <div className="service-info">
              <h3>iHeartRadio</h3>
              <p className="service-description">Requires partnership</p>
            </div>
            <div className="service-actions">
              <button className="service-btn disabled" disabled>
                Not Available
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Services;
